// @flow

import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {useAccount, useNavigation, useLocation, useSidebar} from 'docs/data';
import classNames from 'classnames';
import {motion, AnimatePresence} from 'framer-motion';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {AlignBox} from 'sail/Align';
import {BodyTextProps, Text} from 'sail/Text';
import {UnstyledLink} from 'sail/UnstyledLink';
import {ButtonLink} from 'sail/Button';

import Icon from 'sail/Icon';
import {getExpansionVariants} from 'docs/util/animation';
import {getNavItems} from 'docs/data/navigation/activateNavigation';
import type {
  NavigationProduct,
  NavigationNode,
  NavigationXRay,
} from 'docs/data';

import isEmpty from 'lodash/isEmpty';
import {defineMessages, FormattedMessage} from 'react-intl';
import {SidebarSubtree} from './SidebarSubtree';
import {SidebarLink} from './SidebarLink';
import {SidebarInfoTooltip} from './SidebarInfoTooltip';

const messages = defineMessages({
  overview: {
    id: 'docsShell.sidebarNavigation.overview',
    description:
      'Top of the sidenav, routes to the main page for the category, i.e /docs/payments',
    defaultMessage: 'Overview',
  },
  home: {
    id: 'docsShell.sidebarNavigation.home',
    description: 'Top of the sidenav, routes to /docs',
    defaultMessage: 'Home',
  },
  show: {
    id: 'docsShell.sidebarNavigation.show',
    description: 'Describes the icon used to show the navigation',
    defaultMessage: 'Click to open the sidebar and show the navigation',
  },
  hide: {
    id: 'docsShell.sidebarNavigation.hide',
    description: 'Describes the icon used to collapse the navigation',
    defaultMessage: 'Click to collapse the sidebar and hide the navigation',
  },
});

export const SidebarNavigation = () => {
  const {open, setOpen} = useSidebar();
  const contentRef = useRef();

  return (
    <Box
      flex
      flexGrow={1}
      className="SidebarNavigation-content"
      padding={{left: 12, right: 20, top: 24}}
      boxRef={contentRef}
    >
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{type: 'spring', damping: 100, stiffness: 1000}}
          >
            <OpenSidebar contentRef={contentRef} />
          </motion.div>
        )}
      </AnimatePresence>
      <Box background="white" className="SidebarNavigation-toggle">
        <ButtonLink
          className="SidebarNavigation-toggleIcon"
          icon={open ? 'panelArrowLeft' : 'panelArrowRight'}
          label={
            open ? (
              <FormattedMessage {...messages.hide} />
            ) : (
              <FormattedMessage {...messages.show} />
            )
          }
          color="gray"
          hideLabel
          onClick={() => setOpen((o) => !o)}
        />
      </Box>
    </Box>
  );
};

type OpenSidebarProps = {
  contentRef: React.ElementRef<any>,
};

const OpenSidebar = ({contentRef}: OpenSidebarProps) => {
  const {navigation} = useNavigation();
  const {location} = useLocation();
  const {account} = useAccount();
  const activeTopic = navigation.topics.find((t) => t.active) || {};
  // Find all the product categories for the active topic
  const products = getNavItems(navigation);

  // activeProduct represents the product dropdown of the current article
  // openProduct represents the product dropdown in the sidebar that is open
  // They are independent of each other so users can explore the sidebar without switching articles
  const activeProduct = products.find((p) => p.active) || {};
  const [openProduct, setOpenProduct] = useState(activeProduct);

  const switchOpenProduct = (product: NavigationProduct, scrollUp: boolean) => {
    if (scrollUp) {
      contentRef.current.scrollTop = 0;
    }
    setOpenProduct(product);
  };

  useEffect(() => {
    const product = products.find((p) => p.active) || {};
    // Scroll to the top of the sidebar when going to a top-level page
    switchOpenProduct(product, isEmpty(product));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [location.pathname, account.country, account.locale]);

  const isActiveTopic = location.pathname === activeTopic.href;
  const flattened = products.length === 1;
  return (
    <Box padding={{bottom: 12}}>
      <Group spacing={flattened ? 8 : 12}>
        <OverviewLink
          href={activeTopic.href}
          active={isActiveTopic}
          switchOpenProduct={switchOpenProduct}
          hasDropdowns={!flattened}
          translated={activeTopic.translated}
        />
        {/*
         * If we have one product for this category we have a flat sidebar nav -- do not render dropdowns
         * Otherwise render a dropdown for each product in the topic
         */}
        {flattened ? (
          <SidebarSubtree
            nodes={products[0].children}
            indentLevel={1}
            topLevel
          />
        ) : (
          products.map((product) => (
            <SidebarNavigationProduct
              key={product.text}
              product={product}
              openProduct={openProduct}
              activeProduct={activeProduct}
              handleClick={switchOpenProduct}
            />
          ))
        )}
      </Group>
    </Box>
  );
};

type SidebarNavigationProductProps = {
  product: NavigationProduct,
  openProduct: NavigationProduct,
  activeProduct: NavigationProduct,
  handleClick: (openProduct: NavigationProduct, scrollUp: boolean) => void,
};
const SidebarNavigationProduct = ({
  product,
  openProduct,
  activeProduct,
  handleClick,
}: SidebarNavigationProductProps) => {
  const open = product.text === openProduct.text;
  const dropdownChildren = product.children;
  if (
    dropdownChildren.length === 1 &&
    dropdownChildren[0].children &&
    !dropdownChildren[0].children.length
  ) {
    // Some product dropdowns do not have any nested pages for a subtree (e.g. Testing)
    // Flatten the product so we only show the single child page (without a dropdown)
    const node: NavigationNode = product.children[0];
    return (
      <DropdownLink
        active={node.type === 'link' ? node.selected : false}
        text={product.children[0].text}
        href={node.type === 'link' ? node.href : '#'}
        open={false}
        hasChildren={false}
        handleClick={() => {
          handleClick({}, true);
        }}
        xray={product.xray}
      />
    );
  }
  return (
    <Box className={classNames(product.icon || '')}>
      <DropdownLink
        active={product.text === activeProduct.text}
        open={open}
        text={product.text}
        href={product.href}
        hasChildren
        handleClick={() => {
          handleClick(open ? {} : product, true);
        }}
        xray={product.xray}
      />
      <Box padding={{left: 24}}>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key={product.href}
              variants={getExpansionVariants('split')}
              transition={{type: 'spring', damping: 100, stiffness: 1000}}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
            >
              <SidebarSubtree nodes={product.children} indentLevel={0} />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
};

type DropdownLinkProps = {|
  active: boolean,
  open: boolean,
  hasChildren: boolean,
  text: string | FormattedMessage,
  href: string,
  handleClick: (SyntheticMouseEvent<>) => void,
  xray: ?NavigationXRay,
|};

const DropdownLink = ({
  active,
  open,
  text,
  href,
  hasChildren,
  handleClick,
  xray,
}: DropdownLinkProps) => {
  return (
    <AlignBox
      margin={{left: hasChildren ? 0 : 12}}
      reference={BodyTextProps}
      onClick={handleClick}
    >
      {hasChildren && (
        <Icon
          icon={open ? 'caretDown' : 'caretRight'}
          color={'gray300'}
          size={8}
          style={{marginRight: '4px', cursor: 'pointer'}}
        />
      )}
      <Text
        weight={active ? 'bold' : 'medium'}
        color={active && !hasChildren ? 'blue' : 'default'}
        lineHeight={20}
        size={14}
      >
        <UnstyledLink href={hasChildren ? '' : href}>
          <span style={{marginRight: xray ? '4px' : '0px'}}>{text}</span>
          <SidebarInfoTooltip xray={xray} />
        </UnstyledLink>
      </Text>
    </AlignBox>
  );
};

// Displays a link to the top-level category page (e.g. Business Operations)
type OverviewLinkProps = {|
  active: boolean,
  href: string,
  switchOpenProduct: (product: NavigationProduct, scrollUp: boolean) => void,
  hasDropdowns: boolean,
  translated: ?boolean,
|};

const OverviewLink = ({
  active,
  href,
  switchOpenProduct,
  hasDropdowns,
  translated = null,
}: OverviewLinkProps) => {
  // No href means we have an orphaned page in a non-existent category
  // Display a "Home" link to help reroute the user back
  const text = href ? (
    <FormattedMessage {...messages.overview} />
  ) : (
    <FormattedMessage {...messages.home} />
  );
  return hasDropdowns ? (
    <DropdownLink
      active={active}
      open={false}
      text={text}
      href={href || '/docs'}
      hasChildren={false}
      handleClick={() => switchOpenProduct({}, true)}
      xray={null}
    />
  ) : (
    <Box margin={{left: 12}}>
      <SidebarLink
        active={active}
        href={href || '/docs'}
        translated={translated}
      >
        {text}
      </SidebarLink>
    </Box>
  );
};
